import { DisplayFilters } from './display-filters';
import logger from 'logger';

//
// Collection of helper functions used to add or remove filters from the current filter set
//

export function toggleTag(input: {
  displayFilters: DisplayFilters;
  currentFilters: DisplayFilters;
  tagId: number;
}): DisplayFilters {
  const { displayFilters, currentFilters, tagId } = input;

  const tagExists = currentFilters.tagCategories?.some((tagCategory) =>
    tagCategory.tags.some((tag) => tag.id === tagId),
  );

  return tagExists
    ? removeTagFromFilters({
        currentFilters,
        tagId,
      })
    : addTagToFilters({
        displayFilters,
        currentFilters,
        tagId,
      });
}

export function addTagToFilters(input: {
  displayFilters: DisplayFilters;
  currentFilters: DisplayFilters;
  tagId: number;
}): DisplayFilters {
  const { displayFilters, currentFilters, tagId } = input;

  const updatedFilters = { ...currentFilters };

  // This really should never happen, but it's allowable by the TS types
  if (!displayFilters.tagCategories?.length) {
    return currentFilters;
  }

  // Grab the full category that this tag lives under
  const incomingFilterTagCategory = displayFilters.tagCategories.find(
    (tagCategory) => {
      return tagCategory.tags.some((tag) => tag.id === tagId);
    },
  );

  // Grab the full tag
  const incomingTag = incomingFilterTagCategory?.tags.find(
    (tag) => tag.id === tagId,
  );

  if (!incomingFilterTagCategory || !incomingTag) {
    logger.error({
      msg: 'UnableToFindFilterTagCategory',
      extra: {
        input,
      },
    });

    return currentFilters;
  }

  const existingTagCategoryIndex = updatedFilters.tagCategories?.findIndex(
    (tagCategory) => tagCategory.id === incomingFilterTagCategory.id,
  );

  // If the tag category currently exists on the selected filters, just add the tag
  if (
    existingTagCategoryIndex !== -1 &&
    existingTagCategoryIndex !== undefined
  ) {
    // Defensively ensure that the current tag doesn't exist so it doesn't get double-added
    if (
      !updatedFilters.tagCategories![existingTagCategoryIndex].tags.find(
        (tag) => tag.id === tagId,
      )
    ) {
      updatedFilters.tagCategories![existingTagCategoryIndex].tags.push(
        incomingTag,
      );
    }

    return updatedFilters;
  }

  // At this point we need to add the tag category with the tag
  updatedFilters.tagCategories = [
    ...(updatedFilters.tagCategories || []),
    {
      ...incomingFilterTagCategory,
      tags: [incomingTag],
    },
  ];

  return updatedFilters;
}

export function removeTagFromFilters(input: {
  currentFilters: DisplayFilters;
  tagId: number;
}): DisplayFilters {
  const { currentFilters, tagId } = input;

  const updatedFilters = { ...currentFilters };

  // If there are no tag categories, nothing to remove
  if (!updatedFilters.tagCategories?.length) {
    return currentFilters;
  }

  // Find the tag category that contains the tag to remove
  const tagCategoryIndex = updatedFilters.tagCategories.findIndex(
    (tagCategory) => tagCategory.tags.some((tag) => tag.id === tagId),
  );

  if (tagCategoryIndex === -1) {
    // Tag not found, return current filters
    return currentFilters;
  }

  // Remove the tag from the category's tags array
  const tagCategory = updatedFilters.tagCategories[tagCategoryIndex];
  const filteredTags = tagCategory.tags.filter((tag) => tag.id !== tagId);

  // If there are still tags left in the category, update it; otherwise, remove the category
  if (filteredTags.length > 0) {
    updatedFilters.tagCategories = [
      ...updatedFilters.tagCategories.slice(0, tagCategoryIndex),
      { ...tagCategory, tags: filteredTags },
      ...updatedFilters.tagCategories.slice(tagCategoryIndex + 1),
    ];
  } else {
    updatedFilters.tagCategories = [
      ...updatedFilters.tagCategories.slice(0, tagCategoryIndex),
      ...updatedFilters.tagCategories.slice(tagCategoryIndex + 1),
    ];
  }

  return updatedFilters;
}

export function toggleIngredientsToAvoidFilters(input: {
  displayFilters: DisplayFilters;
  currentFilters: DisplayFilters;
  legacyAllergenItemId: number;
}): DisplayFilters {
  const { displayFilters, currentFilters, legacyAllergenItemId } = input;

  const ingredientExists = currentFilters.ingredientsToAvoid?.some(
    (ingredient) => ingredient.legacyAllergenItemId === legacyAllergenItemId,
  );

  return ingredientExists
    ? removeIngredientsToAvoidToFilters({
        currentFilters,
        legacyAllergenItemId,
      })
    : addIngredientsToAvoidToFilters({
        displayFilters,
        currentFilters,
        legacyAllergenItemId,
      });
}

function addIngredientsToAvoidToFilters(input: {
  displayFilters: DisplayFilters;
  currentFilters: DisplayFilters;
  legacyAllergenItemId: number;
}): DisplayFilters {
  const { displayFilters, currentFilters, legacyAllergenItemId } = input;

  const updatedFilters = { ...currentFilters };

  if (!displayFilters.ingredientsToAvoid?.length) {
    return currentFilters;
  }

  const ingredientToAdd = displayFilters.ingredientsToAvoid.find(
    (ingredient) => ingredient.legacyAllergenItemId === legacyAllergenItemId,
  );

  if (!ingredientToAdd) {
    logger.error({
      msg: 'UnableToFindIngredientToAvoid',
      extra: {
        input,
      },
    });

    return currentFilters;
  }

  // Ensure that this value doesn't already exist so it doesn't get double-added
  const existingIngredients = updatedFilters.ingredientsToAvoid || [];
  const alreadyExists = existingIngredients.some(
    (ingredient) => ingredient.legacyAllergenItemId === legacyAllergenItemId,
  );

  if (!alreadyExists) {
    updatedFilters.ingredientsToAvoid = [
      ...existingIngredients,
      ingredientToAdd,
    ];
  }

  return updatedFilters;
}

export function removeIngredientsToAvoidToFilters(input: {
  currentFilters: DisplayFilters;
  legacyAllergenItemId: number;
}): DisplayFilters {
  const { currentFilters, legacyAllergenItemId } = input;

  const updatedFilters = { ...currentFilters };

  if (!updatedFilters.ingredientsToAvoid?.length) {
    return currentFilters;
  }

  updatedFilters.ingredientsToAvoid = updatedFilters.ingredientsToAvoid.filter(
    (ingredient) => ingredient.legacyAllergenItemId !== legacyAllergenItemId,
  );

  return updatedFilters;
}

export function toggleChefName(input: {
  currentFilters: DisplayFilters;
  chefName: string;
}): DisplayFilters {
  const { currentFilters, chefName } = input;

  const chefExists = currentFilters.chefNames?.find(
    (existingChefName) => existingChefName === chefName,
  );

  return chefExists
    ? removeChefNameFromFilters(input)
    : addChefNameToFilters(input);
}

function addChefNameToFilters(input: {
  currentFilters: DisplayFilters;
  chefName: string;
}): DisplayFilters {
  const { currentFilters, chefName } = input;

  if (currentFilters.chefNames?.includes(chefName)) {
    return currentFilters;
  }

  return {
    ...currentFilters,
    chefNames: [...(currentFilters.chefNames || []), chefName],
  };
}

export function removeChefNameFromFilters(input: {
  currentFilters: DisplayFilters;
  chefName: string;
}): DisplayFilters {
  const { currentFilters, chefName } = input;

  if (!currentFilters.chefNames) {
    return currentFilters;
  }

  return {
    ...currentFilters,
    chefNames: currentFilters.chefNames.filter((name) => name !== chefName),
  };
}

export function getSelectedDietaryTemplateTags(input: {
  currentFilters: DisplayFilters;
}): NonNullable<DisplayFilters['tagCategories']>[number]['tags'] | undefined {
  const { currentFilters } = input;

  const dietaryTemplateTags = input.currentFilters.tagCategories?.find(
    (tagCategory) => tagCategory.logicalId === 'DIETARY_TEMPLATE',
  )?.tags;

  if (!dietaryTemplateTags || dietaryTemplateTags.length === 0) {
    logger.warn({
      msg: 'NoCurrentlySelectedDietaryTemplate',
      extra: {
        currentFilters,
      },
    });

    return undefined;
  }

  return dietaryTemplateTags;
}
